import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import HeroCarousel from "@/components/HeroCarousel";
import { Search, ShieldCheck, Truck, Stethoscope, Lock, Pill, ArrowRight } from "lucide-react";
import { SHIPPING_TIERS } from "@/lib/pharmacy";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  return (
    <div>
      {/* PROMOTIONAL CAROUSEL */}
      <HeroCarousel />

      {/* HERO */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-white via-emerald-50/30 to-white">
        <div className="blz-container grid lg:grid-cols-12 gap-10 py-14 lg:py-20 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
              <ShieldCheck className="h-3.5 w-3.5"/> Licensed pharmacist reviewed
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Certified pharmaceutical solutions.<br/><span className="text-emerald-700">Precision packaged.</span>
            </h1>
            <p className="mt-5 text-slate-600 text-lg max-w-xl">
              Order verified prescription and OTC medications in standardized pack sizes — 50, 100, 150, 200, 300, 600 pills — with transparent flat-tier shipping.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/catalogue" className="btn-primary" data-testid="hero-cta-catalogue">Explore catalogue <ArrowRight className="h-4 w-4"/></Link>
              <Link to="/prescription-info" className="btn-ghost" data-testid="hero-cta-rx">Prescription info</Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden border border-slate-200 aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1580281657527-47f249e8f4df?w=800" alt="Pharma" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="blz-container py-12">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col md:flex-row items-center gap-4">
          <Search className="h-6 w-6 text-slate-400"/>
          <form action="/catalogue" className="flex-1 w-full">
            <input name="q" placeholder="Search medicines, brand, composition..." className="w-full h-11 rounded-full border border-slate-200 px-4 focus:outline-none focus:ring-2 focus:ring-emerald-600" data-testid="home-search-input"/>
          </form>
          <Link to="/catalogue" className="btn-dark whitespace-nowrap" data-testid="home-browse-all">Browse all</Link>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="blz-container py-6">
        <div className="flex justify-between items-end mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Popular categories</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Shop by therapeutic area</h2>
          </div>
          <Link to="/catalogue" className="text-sm text-slate-600 hover:text-slate-900">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {cats.map((c) => (
            <Link key={c.id} to={`/category/${c.slug}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-emerald-600 transition" data-testid={`cat-${c.slug}`}>
              <div className="aspect-square bg-slate-100 overflow-hidden">
                <img src={c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition"/>
              </div>
              <div className="p-3 text-sm font-semibold text-slate-900">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="blz-container py-12">
        <div className="flex justify-between items-end mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Featured</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Popular medications</h2>
          </div>
          <Link to="/catalogue" className="text-sm text-slate-600 hover:text-slate-900">Full catalogue →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {featured.map((p) => <ProductCard key={p.id} product={p}/>)}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-slate-900 text-white">
        <div className="blz-container py-14 grid md:grid-cols-4 gap-6">
          {[
            {icon: ShieldCheck, title: "Licensed & verified", desc: "Pharmacist review on every prescription order."},
            {icon: Truck, title: "Discreet shipping", desc: "Temperature-controlled, tamper-evident packaging."},
            {icon: Stethoscope, title: "Approved supply chain", desc: "Sourced from certified manufacturing facilities."},
            {icon: Lock, title: "Secure checkout", desc: "Encrypted data, PCI-aligned payment handling."},
          ].map(({icon: Icon, title, desc}) => (
            <div key={title}>
              <Icon className="h-6 w-6 text-emerald-400 mb-2"/>
              <div className="font-semibold">{title}</div>
              <div className="text-sm text-slate-400 mt-1">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ preview */}
      <section className="blz-container py-14">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Support</div>
            <h2 className="font-display text-3xl font-bold text-slate-900 mt-1">Frequently asked</h2>
            <p className="text-slate-600 mt-3">Answers to common questions about ordering, shipping and prescription verification.</p>
            <Link to="/faq" className="btn-ghost mt-5" data-testid="home-faq-link">See all FAQs</Link>
          </div>
          <div className="space-y-3">
            {[
              ["Why fixed pack sizes?", "Standardized pack sizes (50/100/150/200/300/600) ensure consistent dosing and enable transparent flat-tier shipping."],
              ["Is a prescription required?", "Some products require a valid prescription. These are clearly marked with an 'Rx' badge on the product page."],
              ["How is shipping calculated?", "Shipping is based on total pills across your entire cart: 50–200 = $15, 201–300 = $25, 301–600 = $35."],
            ].map(([q,a]) => (
              <details key={q} className="rounded-xl border border-slate-200 bg-white p-4 group">
                <summary className="font-semibold text-slate-900 cursor-pointer flex justify-between items-center">{q}<span className="text-emerald-600">+</span></summary>
                <p className="text-sm text-slate-600 mt-3">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
